import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { roleManagementAPI } from '../services/api';

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [permissionMap, setPermissionMap] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const loadPermissions = async () => {
      if (user?.id) {
        try {
          setLoading(true);
          // Get user permissions from role management API
          const response = await roleManagementAPI.getRolePermissions(user.role);
          const userPermissions = response.data.permissions.filter(p => p.granted);
          
          setPermissions(userPermissions);
          
          // Create a map for quick permission lookups
          const map = {};
          userPermissions.forEach(permission => {
            map[permission.name] = true;
            // Also create module.action shortcuts
            map[`${permission.module}.${permission.action}`] = true;
          });
          setPermissionMap(map);
          
        } catch (error) {
          console.error('Error loading permissions:', error);
          setPermissions([]);
          setPermissionMap({});
        }
      } else {
        setPermissions([]);
        setPermissionMap({});
      }
      setLoading(false);
    };

    loadPermissions();
  }, [user]);

  // Check if user has a specific permission
  const hasPermission = (permissionName) => {
    return !!permissionMap[permissionName];
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (...permissionNames) => {
    return permissionNames.some(permission => hasPermission(permission));
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (...permissionNames) => {
    return permissionNames.every(permission => hasPermission(permission));
  };

  // Check permissions by module and action
  const hasModulePermission = (module, action) => {
    return hasPermission(`${module}.${action}`) || hasPermission(`${module}.${action}.all`) || hasPermission(`${module}.${action}.own`) || hasPermission(`${module}.${action}.team`);
  };

  // Get permissions for a specific module
  const getModulePermissions = (module) => {
    return permissions.filter(p => p.module === module);
  };

  // Check if user can perform any action on a module
  const canAccessModule = (module) => {
    return permissions.some(p => p.module === module);
  };

  const value = {
    permissions,
    permissionMap,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasModulePermission,
    getModulePermissions,
    canAccessModule,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;